import { Flex, Box, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Modal, useToast } from '@morressier/alembic';
import {
  buildRowDuplicateMessage,
  findRowLevelDuplicates,
  IResearchFocusAndExpertiseType,
  IUserDetailsStepThreeForm,
  useUserDetailsData
} from '../UserRegistrationSteps/StepThree/signupHook';
import ResearchFocusAndExpertise from '../UserRegistrationSteps/StepThree/ResearchFocusAndExpertise';
import { useUpdatedUserInfoMutation } from 'src/api/signUpMutation';

export type IEditResearchFocusAndExpertiseForm = {
  researchFocusAndExpertise: IResearchFocusAndExpertiseType;
};

const EditResearchFocusAndExpertiseModal = ({
  organizationId,
  data
}: {
  organizationId: string;
  data: IEditResearchFocusAndExpertiseForm;
}) => {
  const updatedMutation = useUpdatedUserInfoMutation();
  const toast = useToast();
  const { configs } = useUserDetailsData(organizationId);
  const [isOpen, setIsOpen] = useState(false);
  const methods = useForm<IUserDetailsStepThreeForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur'
  });
  const { handleSubmit, reset, watch, setError, clearErrors } = methods;
  const onClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleSubmitForm = async (values: IUserDetailsStepThreeForm) => {
    const {
      researchFocusAndExpertise: {
        reviewerInterest,
        unavailabilityPeriodFrom,
        unavailabilityPeriodTo,
        specialtyAndKeywords,
        scandinavianKeywords,
        professionalInterests
      }
    } = values;
    const filteredSpecialtyAndKeywords =
      specialtyAndKeywords?.filter(
        (item: any) => item.specialty !== null || item.keyword !== null
      ) ?? [];

    const liveData =
      specialtyAndKeywords ||
      watch('researchFocusAndExpertise.specialtyAndKeywords') ||
      [];
    const rowDuplicateResult = findRowLevelDuplicates(liveData);
    if (rowDuplicateResult.hasDuplicates) {
      // Clear any previous duplicate errors first
      liveData.forEach((_: any, rowIndex: number) => {
        ['specialty', 'subSpecialty', 'keyword', 'scandinavianKeyword'].forEach(
          fieldKey => {
            clearErrors(
              `researchFocusAndExpertise.specialtyAndKeywords.${rowIndex}.${fieldKey}` as any
            );
          }
        );
      });

      // Set inline errors on each field in duplicate rows
      rowDuplicateResult.duplicateRows.forEach(issue => {
        const rowIndex = issue.rowIndex - 1; // API uses 1-based row numbering
        issue.fieldKeys.forEach(fieldKey => {
          setError(
            `researchFocusAndExpertise.specialtyAndKeywords.${rowIndex}.${fieldKey}` as any,
            {
              type: 'validate',
              message: buildRowDuplicateMessage(issue)
            }
          );
        });
      });

      return;
    }

    const payload = {
      section: 'RESEARCH_FOCUS_AND_EXPERTISE',
      researchFocusAndExpertise: {
        reviewerInterest,
        unavailabilityPeriodFrom:
          reviewerInterest === 'Yes' ? unavailabilityPeriodFrom : '',
        unavailabilityPeriodTo:
          reviewerInterest === 'Yes' ? unavailabilityPeriodTo : '',
        specialtyAndKeywords: filteredSpecialtyAndKeywords?.length
          ? filteredSpecialtyAndKeywords?.map(item => ({
              specialtyName: item?.specialty?.name,
              specialtyId: item?.specialty?.termId,
              subSpecialtyName: item?.subSpecialty?.name,
              subSpecialtyId: item?.subSpecialty?.termId,
              keyword: item?.keyword?.name,
              keywordId: item?.keyword?.termId,
              scandinavianKeyword: item?.scandinavianKeyword?.name,
              scandinavianKeywordId: item?.scandinavianKeyword?.termId
            }))
          : [],
        scandinavianKeywords: scandinavianKeywords,
        professionalInterests
      }
    };

    const response = await updatedMutation.mutateAsync({ payload });
    const message =
      response?.message?.details?.[0]?.message ??
      response?.message?.message ??
      response?.message ??
      'Internal Server Error';

    toast({
      variant: response.isSuccess ? 'success' : 'error',
      title: response.isSuccess ? 'Success' : 'Error',
      description: message,
      isClosable: true
    });

    if (response.isSuccess) {
      setIsOpen(false);
    }
  };
  const resetForm = () => {
    reset({
      ...data,
      researchFocusAndExpertise: {
        ...data.researchFocusAndExpertise,
        specialtyAndKeywords: data.researchFocusAndExpertise
          ?.specialtyAndKeywords.length
          ? data.researchFocusAndExpertise?.specialtyAndKeywords?.map(item => ({
              specialty: { name: item.specialtyName, termId: item.specialtyId },
              subSpecialty: item.subSpecialtyId
                ? {
                    name: item.subSpecialtyName,
                    termId: item.subSpecialtyId
                  }
                : null,
              keyword: { name: item.keyword, termId: item.keywordId },
              scandinavianKeyword: item.scandinavianKeywordId
                ? {
                    name: item.scandinavianKeyword,
                    termId: item.scandinavianKeywordId
                  }
                : null,
              isCustomKeyword: false
            }))
          : [],
        scandinavianKeywordsTemp:
          data?.researchFocusAndExpertise?.scandinavianKeywords?.map(value => {
            return { text: value };
          }),
        professionalInterests:
          data?.researchFocusAndExpertise?.professionalInterests ?? [],
        professionalInterestsTemp:
          data?.researchFocusAndExpertise?.professionalInterests?.map(
            value => ({ text: value })
          ) ?? []
      }
    });
  };

  // keep form in sync with incoming data
  useEffect(() => {
    if (data) {
      resetForm();
    }
  }, [data, reset]);
  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        <Box>
          <FontAwesomeIcon icon={faEdit} />{' '}
        </Box>{' '}
        <Text>Edit Research Interest</Text>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        closeButtonProps={{
          visibility: 'hidden'
        }}
        width="1100px"
      >
        <FormProvider {...methods}>
          <Flex direction="column" px={8} pt={8}>
            <Flex direction="column" gap={3}>
              <Box>
                <FontAwesomeIcon icon={faEdit} size="2xl" />
              </Box>

              <form
                autoComplete="off"
                onSubmit={handleSubmit(handleSubmitForm)}
              >
                <Flex direction="column" gap={5}>
                  <ResearchFocusAndExpertise configs={configs} type="Edit" />

                  <Flex justifyContent="flex-end" gap={3} mb={5}>
                    <Button variant="secondary" onClick={onClose}>
                      Exit Without Saving
                    </Button>
                    <Button type="submit" disabled={updatedMutation.isLoading}>
                      {updatedMutation.isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};

export default EditResearchFocusAndExpertiseModal;
